import React, { useMemo } from 'react';
// @ts-ignore
import { emojify } from 'react-emojione';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useFragment } from 'react-relay/hooks';

import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import { graphql } from 'babel-plugin-relay/macro';
import classnames from 'classnames';
import { ErrorBoundary } from 'embeds/components';
import { muiThemeLuma } from 'embeds/themes';
import { forEach, get, isString, map } from 'lodash';
import moment from 'moment';
import { ChatActivityReason } from 'requests/resources';
import sanitizeHtml from 'sanitize-html';
import { axiosService } from 'ui-model';

import { RolloutEnum } from '@lib/resources/types';
import configService from '@lib/services/configService';

import { REF_PROVISIONING } from 'components/Chat/constants';
import usePermissions from 'hooks/usePermissions';
import IconAvatarBot from 'icons/IconAvatarBot';

import { ChatMessage_EmbedMessage$key } from './__generated__/ChatMessage_EmbedMessage.graphql';
import { ChatMessage_EmbedViewer$key } from './__generated__/ChatMessage_EmbedViewer.graphql';
import AvatarNode from './AvatarNode';
import TypographyMessageDetails from './TypographyMessageDetails';
import { URLify } from './utils/urlify';

const styles = ({
  custom: {
    palette: { colorDarkGray, colorGrayishBlue, colorAccentSunrise, colorAccentPeach, colorMessageBubble },
  },
  palette: {
    common: { white },
    text,
  },
  spacing: { unit },
}: Theme) =>
  createStyles({
    avatarBot: {
      color: '#fff',
      backgroundColor: colorMessageBubble,
      width: 26,
      height: 26,
    },
    attachButton: {
      backgroundColor: `${white} !important`,
      height: 25,
      marginTop: 5,
      padding: `0px ${unit * 2}px`,
    },
    attachButtonLabel: {
      color: `${text.primary} !important`,
      fontSize: 11,
      textTransform: 'initial',
    },
    attachIcon: {
      fontSize: 14,
    },
    chatAudit: {},
    interlocutor: {
      color: text.secondary,
      display: 'inline-block',
      float: 'right',
      fontSize: 13,
      fontWeight: 500,
      padding: `${unit / 2}px ${unit / 4 + 4}px ${unit + 4}px 0px`,
      textAlign: 'right',
      width: '100%',
    },
    internal: {},
    messageContent: {
      color: 'inherit',
      fontWeight: 'inherit',
      width: '100%',
      wordBreak: 'break-word',
      wordWrap: 'break-word',
      '& a': {
        color: 'inherit !important',
      },
    },
    messageIcon: {
      position: 'absolute',
      width: 35,
      height: 35,
      left: -18,
      top: -18,
      border: '1px solid',
      '& span': {
        fontSize: 18,
      },
    },
    messageWrapper: {
      alignItems: 'flex-start',
      backgroundColor: colorGrayishBlue,
      position: 'relative',
      border: '1px',
      borderRadius: '15px 15px 15px 0px',
      color: text.primary,
      display: 'flex',
      flexDirection: 'column',
      fontWeight: 500,
      marginLeft: unit / 2,
      marginRight: 0,
      maxWidth: '80%',
      padding: '10px 15px',
      '&$outbound': {
        backgroundColor: colorMessageBubble,
        borderRadius: '15px 15px 0px 15px !important',
        color: `${white} !important`,
        alignItems: 'flex-end !important',
        marginLeft: '0px !important',
        marginRight: `${unit / 2}px !important`,
      },
      '&$internal': {
        backgroundColor: `${colorAccentSunrise} !important`,
        border: `1px solid ${colorAccentPeach} !important`,
        color: `${text.primary} !important`,
      },
    },
    outbound: {},
    primary: {},
    pullBottom: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
    },
    root: {
      display: 'flex',
      flexDirection: 'column',
      marginBottom: 5,
    },
    wrapper: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      '&$outbound': {
        justifyContent: 'flex-end !important',
      },
      '&$chatAudit': {
        color: colorDarkGray,
        padding: unit * 2,
        fontSize: 14,
        fontWeight: 500,
        display: 'inline-block',
        textAlign: 'center',
      },
    },
    '@keyframes blink': {
      '50%': {
        transform: 'scale(1.5)',
      },
    },
  });

const messages = defineMessages({
  messageSuppresed: {
    id: 'deprecated.fragments.ChatMessage.messageSuppresed',
    defaultMessage: 'Message suppressed for patient privacy',
  },
  fileUploading: {
    id: 'deprecated.fragments.ChatMessage.fileUploading',
    defaultMessage: 'File uploading...',
  },
  fileAttached: {
    id: 'deprecated.fragments.ChatMessage.fileAttached',
    defaultMessage: 'File attached',
  },
  viaLuma: {
    id: 'deprecated.fragments.ChatMessage.viaLuma',
    defaultMessage: 'via Luma',
  },
});

// TODO move to lib once it's tested properly in prod
// and cleanup on patient-app: https://github.com/lumahealthhq/patient-web-app/pull/1714
const parseMessageContent = (messageHtml: string, disableLinks: boolean) => {
  let result = messageHtml;
  if (disableLinks) {
    result = sanitizeHtml(result, {
      transformTags: {
        a: 'span',
      },
    });
  } else {
    result = URLify(messageHtml);
  }
  return result;
};

/**
 * Replace ChatActivity close reason IDs by their text
 *
 * @param s
 * @param chatActivityReasons
 */
export const replaceChatActivityReasonKeys = (text: string, chatActivityReasons?: ChatActivityReason[]) => {
  let result = text;
  forEach(chatActivityReasons, (reason) => {
    result = result.replace(`Reason: ${reason._id}`, `Reason: ${reason.text.original}`);
  });

  // Handle replacements for legacy reasons
  // TODO remove once migration in BE is complete
  forEach(
    [
      {
        _id: 'no-further-action',
        text: 'No further action needed',
      },
      {
        _id: 'not-relevant',
        text: 'This isn’t relevant to the patient’s care',
      },
      {
        _id: 'spam',
        text: 'This is spam',
      },
      {
        _id: 'offensive',
        text: 'This message is offensive',
      },
      {
        _id: 'not-actionable',
        text: 'This isn’t actionable',
      },
      {
        _id: 'other',
        text: 'Other',
      },
    ],
    (reason) => {
      result = result.replace(`Reason: ${reason._id}`, `Reason: ${reason.text}`);
    },
  );
  return result;
};

export interface ChatMessageProps extends WithStyles<typeof styles> {
  fragmentRefViewer: ChatMessage_EmbedViewer$key;
  fragmentRef: ChatMessage_EmbedMessage$key;
  chatActivityReasons?: ChatActivityReason[];
  retriedChannels: { name: string; reason: string }[];
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  classes,
  fragmentRefViewer,
  fragmentRef,
  chatActivityReasons,
  retriedChannels,
}) => {
  const token = axiosService.defaults.headers.common['x-access-token'];

  const viewer = useFragment<ChatMessage_EmbedViewer$key>(
    graphql`
      fragment ChatMessage_EmbedViewer on User {
        user {
          salesforceData {
            respectProvisioning
          }
          setting {
            settings {
              communication {
                suppressMessagesByProvisioning
              }
            }
          }
        }
      }
    `,
    fragmentRefViewer,
  );

  const message = useFragment<ChatMessage_EmbedMessage$key>(
    graphql`
      fragment ChatMessage_EmbedMessage on Message {
        automated
        createdAt
        oid_file
        ref
        text
        type
        visibility
        mentions {
          mention
        }
        createdBy(includeDeleted: true) {
          name
          ...AvatarNode_EmbedNode
        }
        patient(includeDeleted: true) {
          name
          ...AvatarNode_EmbedNode
        }
        ...TypographyMessageDetails_EmbedMessage
      }
    `,
    fragmentRef,
  );

  const { formatMessage } = useIntl();
  const { hasAccessToProduct } = usePermissions();

  // WORKAROUND Switch type in case it's the patient instead of staff user the one receiving messages
  const isOutbound = message.type === 'outbound';

  const interlocutor = useMemo(() => {
    if (message.type === 'inbound') {
      return message.patient;
    }
    return message.createdBy;
  }, [message]);

  let text = message.text || '';

  // Replace ChatActivity close reason IDs by their text
  text = replaceChatActivityReasonKeys(text, chatActivityReasons);

  let messageHtml = text || '';

  if (message.type === 'outbound' || message.ref === 'chat') {
    // Links & URLs clickable for `internal` messages only
    const disableLinks = !hasAccessToProduct(RolloutEnum.ChatInternalLinks) || message.visibility !== 'internal';
    messageHtml = parseMessageContent(messageHtml, disableLinks);

    if (message.mentions && message.mentions.length > 0) {
      const mentionsPattern = new RegExp(`\\B(${map(message.mentions, 'mention').join('|')})\\b`, 'g');

      messageHtml = messageHtml.replace(
        mentionsPattern,
        (mention) =>
          `<span style="background-color: #fff; border-radius: 12px; color: #3460C5; font-weight: inherit; padding: 0px 4px; white-space: normal">
            ${mention}
          </span>`,
      );
    }

    // highlight templates
    messageHtml = messageHtml.replace(
      /({{[^}]+}})/gm,
      `<span style="background-color: #f1f1f1; border-radius: 4px; color: ${muiThemeLuma.custom.palette.colorDarkCyan}; font-weight: 400;">$1</span>`,
    );
  }

  // Respect provisioning ON + Setting is turned ON:
  // Users and providers who are provisioned for Hub will only see messages in Hub for other products/features they are provisioned for.
  if (
    get(viewer, 'user.salesforceData.respectProvisioning', false) &&
    get(viewer, 'user.setting.settings.communication.suppressMessagesByProvisioning', false) &&
    message.ref &&
    !hasAccessToProduct(get(get(REF_PROVISIONING, message.ref), 'provisioning'))
  ) {
    messageHtml = formatMessage(messages.messageSuppresed);
  }

  messageHtml = messageHtml.replace(
    /(:[\w]+:)/gm,
    (match) => `<span style="font-size: 14px;">${emojify(match, { output: 'unicode' })}</span>`,
  );

  return (
    <div className={classes.root}>
      {message.ref === 'chatAuditTz' ? (
        <Typography
          className={classnames(classes.wrapper, classes.chatAudit)}
          component="div"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: `${text} — ${moment(message.createdAt as string).format('lll')}` }}
        />
      ) : (
        <div className={classnames(classes.wrapper, { [classes.outbound]: isOutbound })}>
          {!isOutbound && interlocutor && (
            <div className={classes.pullBottom}>
              <AvatarNode fragmentRef={interlocutor} includeIsOnline={false} includeTooltip={false} size="xsmall" />
            </div>
          )}
          <div
            className={classnames(classes.messageWrapper, {
              [classes.outbound]: isOutbound,
              [classes.internal]: message.visibility === 'internal',
            })}
          >
            <div
              className={classes.messageContent}
              dangerouslySetInnerHTML={{ __html: messageHtml }} // eslint-disable-line react/no-danger
            />
            <TypographyMessageDetails
              retriedChannels={retriedChannels}
              fragmentRef={message}
              variant="captionBold"
              color={isOutbound && message.visibility !== 'internal' ? 'white' : 'primary'}
            />
            {message.oid_file && (
              <Button
                classes={{ root: classes.attachButton, label: classes.attachButtonLabel }}
                disabled={!isString(message.oid_file)}
                href={`${configService.getApiBasePath()}/fileUploads/${message.oid_file}/file?token=${token}`}
                target="blank"
                variant="contained"
              >
                <AttachFileIcon className={classes.attachIcon} />
                {!isString(message.oid_file)
                  ? formatMessage(messages.fileUploading)
                  : formatMessage(messages.fileAttached)}
              </Button>
            )}
          </div>
          {isOutbound && (
            <div className={classes.pullBottom}>
              {message.automated || !interlocutor ? (
                <Avatar className={classes.avatarBot}>
                  <IconAvatarBot fontSize="small" />
                </Avatar>
              ) : (
                <AvatarNode fragmentRef={interlocutor} includeIsOnline={false} includeTooltip={false} size="xsmall" />
              )}
            </div>
          )}
        </div>
      )}
      {isOutbound && message.ref !== 'chatAuditTz' && interlocutor && (
        <Typography className={classes.interlocutor}>
          {message.automated ? formatMessage(messages.viaLuma) : interlocutor.name}
        </Typography>
      )}
    </div>
  );
};

export default withStyles(styles, { name: 'LumaChatMessage' })((props: ChatMessageProps) => (
  <ErrorBoundary>
    <ChatMessage {...props} />
  </ErrorBoundary>
));
